import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api, { trainingsApi, staffApi, TrainingType, TrainingStats } from '../services/api';
import { BookOpen,
  GraduationCap,
  Plus,
  Search,
  AlertTriangle,
  CheckCircle,
  Clock,
  Users,
  Award,
  Calendar,
  Loader2,
  Edit2,
  X,
  PlayCircle,
  BarChart3,
  Filter,
  ExternalLink,
  Save,
} from 'lucide-react';
import HelpButton from '../components/HelpButton';
import clsx from 'clsx';

interface Employee {
  id: string;
  firstName: string;
  lastName: string;
  primaryRole: string;
}

export default function TrainingsPage() {
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [notice, setNotice] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);
  const say = (tone: 'success' | 'error', text: string) => { setNotice({ tone, text }); setTimeout(() => setNotice(null), 4500); };
  const [showAddTypeModal, setShowAddTypeModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedTrainingType, setSelectedTrainingType] = useState<TrainingType | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'onboarding' as const,
    isRequired: true,
    requiredForRoles: [] as string[],
    validityMonths: undefined as number | undefined,
    estimatedMinutes: undefined as number | undefined,
    trainingUrl: '',
  });
  const [showEditModal, setShowEditModal] = useState(false);
  const [courseType, setCourseType] = useState<TrainingType | null>(null);
  const [assignData, setAssignData] = useState({
    employeeId: '',
    trainingTypeId: '',
    dueDate: '',
  });

  // Fetch training types
  const { data: trainingTypes, isLoading: loadingTypes } = useQuery({
    queryKey: ['training-types', selectedCategory],
    queryFn: async () => {
      const response = await trainingsApi.getTypes({
        category: selectedCategory === 'all' ? undefined : selectedCategory,
      });
      return response.data?.data || [];
    },
  });

  // Fetch training stats
  const { data: stats } = useQuery({
    queryKey: ['training-stats'],
    queryFn: async () => {
      const response = await trainingsApi.getStats();
      return response.data?.data as TrainingStats;
    },
  });

  // Fetch expiring trainings
  const { data: expiringTrainings } = useQuery({
    queryKey: ['expiring-trainings'],
    queryFn: async () => {
      const response = await trainingsApi.getExpiring(30);
      return response.data?.data || [];
    },
  });

  // Fetch incomplete trainings
  const { data: incompleteTrainings } = useQuery({
    queryKey: ['incomplete-trainings'],
    queryFn: async () => {
      const response = await trainingsApi.getIncomplete();
      return response.data?.data || [];
    },
  });

  // Fetch employees for assignment
  const { data: employees } = useQuery({
    queryKey: ['employees-for-training'],
    queryFn: async () => {
      const response = await staffApi.getAll();
      return response.data?.data || response.data || [];
    },
  });

  // Create training type mutation
  const createTypeMutation = useMutation({
    mutationFn: (data: typeof formData) => trainingsApi.createType(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['training-types'] });
      queryClient.invalidateQueries({ queryKey: ['training-stats'] });
      setShowAddTypeModal(false);
      resetForm();
    },
    onError: (error: any) => {
      say('error', error.response?.data?.error || 'Could not create the training — try again.');
    },
  });

  // Update training type mutation
  const updateTypeMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: typeof formData }) =>
      trainingsApi.updateType(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['training-types'] });
      queryClient.invalidateQueries({ queryKey: ['training-stats'] });
      setShowEditModal(false);
      setSelectedTrainingType(null);
      resetForm();
    },
    onError: (error: any) => {
      say('error', error.response?.data?.error || 'Could not save the change — try again.');
    },
  });

  // Assign training mutation
  const assignMutation = useMutation({
    mutationFn: (data: typeof assignData) => trainingsApi.assignTraining(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['training-stats'] });
      queryClient.invalidateQueries({ queryKey: ['incomplete-trainings'] });
      setShowAssignModal(false);
      setAssignData({ employeeId: '', trainingTypeId: '', dueDate: '' });
      say('success', 'Training assigned.');
    },
    onError: (error: any) => {
      say('error', error.response?.data?.error || 'Could not assign the training — try again.');
    },
  });

  // Seed default training types
  const seedMutation = useMutation({
    mutationFn: () => trainingsApi.seedDefaultTypes(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['training-types'] });
      queryClient.invalidateQueries({ queryKey: ['training-stats'] });
      say('success', 'Starter trainings added.');
    },
    onError: (error: any) => {
      say('error', error.response?.data?.error || 'Could not add the starter set — try again.');
    },
  });

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      category: 'onboarding',
      isRequired: true,
      requiredForRoles: [],
      validityMonths: undefined,
      estimatedMinutes: undefined,
      trainingUrl: '',
    });
  };

  const openEditModal = (type: TrainingType) => {
    setSelectedTrainingType(type);
    setFormData({
      name: type.name,
      description: type.description || '',
      category: type.category as any,
      isRequired: type.isRequired,
      requiredForRoles: type.requiredForRoles || [],
      validityMonths: type.validityMonths || undefined,
      estimatedMinutes: type.estimatedMinutes || undefined,
      trainingUrl: type.trainingUrl || '',
    });
    setShowEditModal(true);
  };

  const categories = ['all', 'onboarding', 'safety', 'service', 'technical', 'compliance'];
  const roles = ['server', 'cook', 'host', 'bartender', 'manager'];

  const filteredTypes = (trainingTypes || []).filter((type: TrainingType) =>
    type.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    type.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'onboarding': return 'bg-blue-100 text-blue-700';
      case 'safety': return 'bg-red-100 text-red-700';
      case 'service': return 'bg-purple-100 text-purple-700';
      case 'technical': return 'bg-amber-100 text-amber-700';
      case 'compliance': return 'bg-green-100 text-green-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  return (
    <div className="p-6 space-y-6">
      {notice && (
        <div className={`rounded-lg px-4 py-2.5 text-sm ${notice.tone === 'success' ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>{notice.text}</div>
      )}
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Training Management</h1>
            <p className="text-slate-500">Track and manage employee trainings</p>
          </div>
          <HelpButton featureId="trainings" />
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowAssignModal(true)}
            className="btn btn-secondary flex items-center gap-2"
          >
            <Users className="w-4 h-4" />
            Assign Training
          </button>
          <button
            onClick={() => setShowAddTypeModal(true)}
            className="btn btn-primary flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Training Type
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <GraduationCap className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Training Types</p>
              <p className="text-2xl font-bold">{stats?.totalTrainingTypes || 0}</p>
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Completion Rate</p>
              <p className="text-2xl font-bold">{stats?.completionRate || 0}%</p>
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Pending</p>
              <p className="text-2xl font-bold">{stats?.pendingTrainings || 0}</p>
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Overdue</p>
              <p className="text-2xl font-bold text-red-600">{stats?.overdueTrainings || 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Alerts Section */}
      {((expiringTrainings && expiringTrainings.length > 0) || (incompleteTrainings && incompleteTrainings.length > 0)) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Expiring Trainings */}
          {expiringTrainings && expiringTrainings.length > 0 && (
            <div className="card">
              <div className="p-4 border-b border-slate-200 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-amber-600" />
                <h2 className="font-semibold">Expiring Within 30 Days</h2>
                <span className="badge bg-amber-100 text-amber-700 ml-auto">
                  {expiringTrainings.length}
                </span>
              </div>
              <div className="divide-y divide-slate-100 max-h-64 overflow-y-auto">
                {expiringTrainings.slice(0, 5).map((item: any) => (
                  <div key={item.id} className="p-3 flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm">
                        {item.employee?.firstName} {item.employee?.lastName}
                      </p>
                      <p className="text-xs text-slate-500">{item.trainingType?.name}</p>
                    </div>
                    <span className="text-xs text-amber-600 font-medium">
                      Expires {new Date(item.expiresAt).toLocaleDateString()}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Incomplete Required Trainings */}
          {incompleteTrainings && incompleteTrainings.length > 0 && (
            <div className="card">
              <div className="p-4 border-b border-slate-200 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-600" />
                <h2 className="font-semibold">Incomplete Required Trainings</h2>
                <span className="badge bg-red-100 text-red-700 ml-auto">
                  {incompleteTrainings.length}
                </span>
              </div>
              <div className="divide-y divide-slate-100 max-h-64 overflow-y-auto">
                {incompleteTrainings.slice(0, 5).map((item: any) => (
                  <div key={item.id} className="p-3 flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm">
                        {item.employee?.firstName} {item.employee?.lastName}
                      </p>
                      <p className="text-xs text-slate-500">{item.trainingType?.name}</p>
                    </div>
                    <span className={clsx(
                      'text-xs font-medium',
                      item.status === 'pending' ? 'text-amber-600' : 'text-blue-600'
                    )}>
                      {item.status === 'pending' ? 'Not Started' : 'In Progress'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            placeholder="Search training types..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input pl-10"
          />
        </div>
        <div className="flex gap-2 overflow-x-auto">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={clsx(
                'px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors capitalize',
                selectedCategory === category
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-slate-600 hover:bg-slate-100'
              )}
            >
              {category === 'all' ? 'All Categories' : category}
            </button>
          ))}
        </div>
      </div>

      {/* Training Types Grid */}
      {loadingTypes ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      ) : filteredTypes.length === 0 ? (
        <div className="card p-12 text-center">
          <GraduationCap className="w-12 h-12 mx-auto mb-3 text-slate-300" />
          <h3 className="text-lg font-medium text-slate-900 mb-2">No Training Types Found</h3>
          <p className="text-slate-500 mb-4">
            {(trainingTypes || []).length === 0
              ? 'Get started by adding default training types or creating your own.'
              : 'No training types match your search.'}
          </p>
          {(trainingTypes || []).length === 0 && (
            <button
              onClick={() => seedMutation.mutate()}
              disabled={seedMutation.isPending}
              className="btn btn-primary"
            >
              {seedMutation.isPending ? 'Adding...' : 'Add Default Training Types'}
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTypes.map((type: TrainingType) => (
            <div key={type.id} className="card p-4 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                    <GraduationCap className="w-5 h-5 text-slate-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">{type.name}</h3>
                    <span className={clsx('badge text-xs capitalize', getCategoryColor(type.category))}>
                      {type.category}
                    </span>
                  </div>
                </div>
                {type.isRequired && (
                  <span className="badge bg-red-100 text-red-700 text-xs">Required</span>
                )}
              </div>

              {type.description && (
                <p className="text-sm text-slate-500 mb-3 line-clamp-2">{type.description}</p>
              )}

              <div className="flex flex-wrap gap-2 mb-3">
                {type.validityMonths && (
                  <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded">
                    Valid for {type.validityMonths} months
                  </span>
                )}
                {type.estimatedMinutes && (
                  <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded">
                    ~{type.estimatedMinutes} min
                  </span>
                )}
              </div>

              {type.requiredForRoles && type.requiredForRoles.length > 0 && (
                <div className="mb-3">
                  <p className="text-xs text-slate-500 mb-1">Required for:</p>
                  <div className="flex flex-wrap gap-1">
                    {type.requiredForRoles.map((role) => (
                      <span key={role} className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded capitalize">
                        {role}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Training URL link */}
              {type.trainingUrl && (
                <div className="mb-3">
                  <a
                    href={type.trainingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 hover:underline"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    View Training Resource
                  </a>
                </div>
              )}

              <div className="mb-3">
                <button
                  onClick={() => setCourseType(type)}
                  className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 hover:underline"
                >
                  <BookOpen className="w-3.5 h-3.5" />
                  {(type as any).content?.lessons?.length ? `Course · ${(type as any).content.lessons.length} lessons${(type as any).content?.quiz?.questions?.length ? ` + quiz` : ''}` : 'Build course (AI)'}
                </button>
              </div>

              <div className="flex items-center gap-2 pt-3 border-t border-slate-100">
                <button
                  onClick={() => {
                    setAssignData({ ...assignData, trainingTypeId: type.id });
                    setShowAssignModal(true);
                  }}
                  className="btn btn-sm btn-secondary flex-1"
                >
                  Assign
                </button>
                <button
                  onClick={() => openEditModal(type)}
                  className="btn btn-sm btn-ghost"
                  title="Edit training type"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Training Type Modal */}
      {courseType && <CourseModal type={courseType} onClose={() => setCourseType(null)} />}
      {showAddTypeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <h2 className="text-lg font-semibold">Add Training Type</h2>
              <button
                onClick={() => {
                  setShowAddTypeModal(false);
                  resetForm();
                }}
                className="p-2 hover:bg-slate-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                createTypeMutation.mutate(formData);
              }}
              className="p-4 space-y-4"
            >
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Name *</label>
                <input
                  type="text"
                  className="input w-full"
                  placeholder="e.g., Food Safety Training"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                <textarea
                  className="input w-full"
                  rows={2}
                  placeholder="Brief description of the training"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Category *</label>
                  <select
                    className="input w-full"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value as any })}
                  >
                    <option value="onboarding">Onboarding</option>
                    <option value="safety">Safety</option>
                    <option value="service">Service</option>
                    <option value="technical">Technical</option>
                    <option value="compliance">Compliance</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Validity (months)</label>
                  <input
                    type="number"
                    className="input w-full"
                    placeholder="e.g., 12"
                    min={1}
                    value={formData.validityMonths || ''}
                    onChange={(e) => setFormData({ ...formData, validityMonths: e.target.value ? parseInt(e.target.value) : undefined })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Est. Duration (min)</label>
                  <input
                    type="number"
                    className="input w-full"
                    placeholder="e.g., 30"
                    min={1}
                    value={formData.estimatedMinutes || ''}
                    onChange={(e) => setFormData({ ...formData, estimatedMinutes: e.target.value ? parseInt(e.target.value) : undefined })}
                  />
                </div>

                <div className="flex items-center">
                  <label className="flex items-center gap-2 cursor-pointer mt-6">
                    <input
                      type="checkbox"
                      checked={formData.isRequired}
                      onChange={(e) => setFormData({ ...formData, isRequired: e.target.checked })}
                      className="w-4 h-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm font-medium text-slate-700">Required training</span>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Required for Roles</label>
                <div className="flex flex-wrap gap-2">
                  {roles.map((role) => (
                    <label key={role} className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.requiredForRoles.includes(role)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData({ ...formData, requiredForRoles: [...formData.requiredForRoles, role] });
                          } else {
                            setFormData({ ...formData, requiredForRoles: formData.requiredForRoles.filter(r => r !== role) });
                          }
                        }}
                        className="w-4 h-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm text-slate-700 capitalize">{role}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Training URL</label>
                <input
                  type="url"
                  className="input w-full"
                  placeholder="https://www.servsafe.com/..."
                  value={formData.trainingUrl}
                  onChange={(e) => setFormData({ ...formData, trainingUrl: e.target.value })}
                />
                <p className="text-xs text-slate-500 mt-1">Link to external training resource (ServSafe, TIPS, OSHA, etc.)</p>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddTypeModal(false);
                    resetForm();
                  }}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={createTypeMutation.isPending}
                >
                  {createTypeMutation.isPending ? 'Creating...' : 'Create Training Type'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Assign Training Modal */}
      {showAssignModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <h2 className="text-lg font-semibold">Assign Training</h2>
              <button
                onClick={() => {
                  setShowAssignModal(false);
                  setAssignData({ employeeId: '', trainingTypeId: '', dueDate: '' });
                }}
                className="p-2 hover:bg-slate-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                assignMutation.mutate(assignData);
              }}
              className="p-4 space-y-4"
            >
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Employee *</label>
                <select
                  className="input w-full"
                  value={assignData.employeeId}
                  onChange={(e) => setAssignData({ ...assignData, employeeId: e.target.value })}
                  required
                >
                  <option value="">Select employee...</option>
                  {(employees || []).map((emp: Employee) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.firstName} {emp.lastName} ({emp.primaryRole})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Training Type *</label>
                <select
                  className="input w-full"
                  value={assignData.trainingTypeId}
                  onChange={(e) => setAssignData({ ...assignData, trainingTypeId: e.target.value })}
                  required
                >
                  <option value="">Select training...</option>
                  {(trainingTypes || []).map((type: TrainingType) => (
                    <option key={type.id} value={type.id}>
                      {type.name} ({type.category})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Due Date</label>
                <input
                  type="date"
                  className="input w-full"
                  value={assignData.dueDate}
                  onChange={(e) => setAssignData({ ...assignData, dueDate: e.target.value })}
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
                <button
                  type="button"
                  onClick={() => {
                    setShowAssignModal(false);
                    setAssignData({ employeeId: '', trainingTypeId: '', dueDate: '' });
                  }}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={assignMutation.isPending}
                >
                  {assignMutation.isPending ? 'Assigning...' : 'Assign Training'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Training Type Modal */}
      {showEditModal && selectedTrainingType && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <h2 className="text-lg font-semibold">Edit Training Type</h2>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedTrainingType(null);
                  resetForm();
                }}
                className="p-2 hover:bg-slate-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (selectedTrainingType) {
                  updateTypeMutation.mutate({ id: selectedTrainingType.id, data: formData });
                }
              }}
              className="p-4 space-y-4"
            >
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Name *</label>
                <input
                  type="text"
                  className="input w-full"
                  placeholder="e.g., Food Safety Training"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                <textarea
                  className="input w-full"
                  rows={2}
                  placeholder="Brief description of the training"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Category *</label>
                  <select
                    className="input w-full"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value as any })}
                  >
                    <option value="onboarding">Onboarding</option>
                    <option value="safety">Safety</option>
                    <option value="service">Service</option>
                    <option value="technical">Technical</option>
                    <option value="compliance">Compliance</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Validity (months)</label>
                  <input
                    type="number"
                    className="input w-full"
                    placeholder="e.g., 12"
                    min={1}
                    value={formData.validityMonths || ''}
                    onChange={(e) => setFormData({ ...formData, validityMonths: e.target.value ? parseInt(e.target.value) : undefined })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Est. Duration (min)</label>
                  <input
                    type="number"
                    className="input w-full"
                    placeholder="e.g., 30"
                    min={1}
                    value={formData.estimatedMinutes || ''}
                    onChange={(e) => setFormData({ ...formData, estimatedMinutes: e.target.value ? parseInt(e.target.value) : undefined })}
                  />
                </div>

                <div className="flex items-center">
                  <label className="flex items-center gap-2 cursor-pointer mt-6">
                    <input
                      type="checkbox"
                      checked={formData.isRequired}
                      onChange={(e) => setFormData({ ...formData, isRequired: e.target.checked })}
                      className="w-4 h-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm font-medium text-slate-700">Required training</span>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Required for Roles</label>
                <div className="flex flex-wrap gap-2">
                  {roles.map((role) => (
                    <label key={role} className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.requiredForRoles.includes(role)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData({ ...formData, requiredForRoles: [...formData.requiredForRoles, role] });
                          } else {
                            setFormData({ ...formData, requiredForRoles: formData.requiredForRoles.filter(r => r !== role) });
                          }
                        }}
                        className="w-4 h-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm text-slate-700 capitalize">{role}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Training URL</label>
                <input
                  type="url"
                  className="input w-full"
                  placeholder="https://www.servsafe.com/..."
                  value={formData.trainingUrl}
                  onChange={(e) => setFormData({ ...formData, trainingUrl: e.target.value })}
                />
                <p className="text-xs text-slate-500 mt-1">Link to external training resource (ServSafe, TIPS, OSHA, etc.)</p>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedTrainingType(null);
                    resetForm();
                  }}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary flex items-center gap-2"
                  disabled={updateTypeMutation.isPending}
                >
                  {updateTypeMutation.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  {updateTypeMutation.isPending ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}


// Course builder + player: AI-generates lessons and a quiz from a pasted SOP,
// translates for the crew, and runs the knowledge check — the Opus layer.
function CourseModal({ type, onClose }: { type: any; onClose: () => void }) {
  const qc = useQueryClient();
  const [source, setSource] = useState('');
  const [busy, setBusy] = useState('');
  const [note, setNote] = useState('');
  const [lang, setLang] = useState('Spanish');
  const [viewLang, setViewLang] = useState<string>('original');
  const [answers, setAnswers] = useState<Record<number, number>>({});
  const [quizResult, setQuizResult] = useState<any>(null);

  const { data: fresh } = useQuery({
    queryKey: ['training-type-content', type.id],
    queryFn: async () => {
      const res = await api.get('/v1/trainings/types');
      const list = res.data?.data || res.data || [];
      return (Array.isArray(list) ? list : []).find((t: any) => t.id === type.id) || type;
    },
  });
  const content = (fresh as any)?.content || null;
  const shown = viewLang !== 'original' && content?.translations?.[viewLang] ? content.translations[viewLang] : content;

  const run = async (what: string, fn: () => Promise<any>) => {
    setBusy(what); setNote('');
    try { await fn(); qc.invalidateQueries({ queryKey: ['training-type-content', type.id] }); qc.invalidateQueries({ queryKey: ['training-types'] }); }
    catch (e: any) { setNote(e?.response?.data?.error || `${what} failed`); }
    finally { setBusy(''); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end md:items-center justify-center z-50">
      <div className="bg-white rounded-t-2xl md:rounded-xl shadow-xl w-full md:max-w-2xl max-h-[90vh] overflow-y-auto p-5 md:p-6 pb-safe">
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-xl font-bold">{type.name} — course</h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-xs text-slate-500 mb-4">Short mobile lessons + a knowledge check, generated from your own SOP. Passing the quiz completes the training automatically.</p>
        {note && <div className="mb-3 p-2.5 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{note}</div>}

        {!content?.lessons?.length ? (
          <div className="space-y-3">
            <label className="block text-sm font-medium text-slate-700">Paste the SOP, handbook section, or notes:</label>
            <textarea className="input w-full" rows={8} value={source} onChange={(e) => setSource(e.target.value)}
              placeholder="e.g. Opening procedure: arrive 6:00, ovens on first, water bath to 190°…" />
            <button
              className="btn btn-primary"
              disabled={source.trim().length < 40 || !!busy}
              onClick={() => run('Generating', () => api.post(`/v1/trainings/types/${type.id}/generate`, { sourceText: source }))}
            >
              {busy === 'Generating' ? 'Writing the course…' : 'Generate course with AI'}
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-2 flex-wrap">
              <select className="input input-sm w-36" value={viewLang} onChange={(e) => setViewLang(e.target.value)}>
                <option value="original">Original</option>
                {Object.keys(content.translations || {}).map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
              <input className="input input-sm w-28" value={lang} onChange={(e) => setLang(e.target.value)} placeholder="Spanish" />
              <button className="btn btn-sm btn-secondary" disabled={!!busy}
                onClick={() => run('Translating', () => api.post(`/v1/trainings/types/${type.id}/translate`, { language: lang }))}>
                {busy === 'Translating' ? 'Translating…' : `Translate to ${lang}`}
              </button>
              <button className="btn btn-sm btn-ghost" disabled={!!busy}
                onClick={() => run('Clearing', () => api.patch(`/v1/trainings/types/${type.id}`, { content: null }))}>
                Rebuild from scratch
              </button>
            </div>

            {(shown?.lessons || []).map((l: any, i: number) => (
              <div key={i} className="card p-4">
                <p className="text-xs text-slate-400 mb-1">Lesson {i + 1}{typeof l.startSec === 'number' ? ` · at ${Math.floor(l.startSec / 60)}:${String(l.startSec % 60).padStart(2, '0')} in the video` : ''}</p>
                <p className="font-semibold text-slate-900">{l.title}</p>
                <LessonImage path={l.imageUrl || content?.lessons?.[i]?.imageUrl} alt={l.title} />
                <p className="text-sm text-slate-600 mt-2 whitespace-pre-wrap">{l.body}</p>
                {l.proTip && <p className="text-xs mt-2 rounded-lg bg-emerald-50 text-emerald-800 px-3 py-2">💡 {l.proTip}</p>}
                {l.pitfall && <p className="text-xs mt-1.5 rounded-lg bg-amber-50 text-amber-800 px-3 py-2">⚠️ {l.pitfall}</p>}
              </div>
            ))}

            {shown?.quiz?.questions?.length > 0 && (
              <div className="card p-4">
                <p className="text-xs uppercase tracking-wide text-slate-400 mb-2">Knowledge check · pass at {shown.quiz.passPct || 80}%</p>
                {shown.quiz.questions.map((q: any, qi: number) => (
                  <div key={qi} className="mb-3">
                    <p className="text-sm font-medium text-slate-800">{qi + 1}. {q.q}</p>
                    <div className="mt-1 space-y-1">
                      {q.options.map((o: string, oi: number) => (
                        <label key={oi} className="flex items-center gap-2 text-sm text-slate-600">
                          <input type="radio" name={`q${qi}`} checked={answers[qi] === oi} onChange={() => setAnswers((a) => ({ ...a, [qi]: oi }))} />
                          {o}
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
                {quizResult && (
                  <p className={`text-sm font-medium ${quizResult.passed ? 'text-emerald-700' : 'text-red-600'}`}>
                    {quizResult.right}/{quizResult.total} — {quizResult.score}% {quizResult.passed ? '· passed, training completed' : `· below ${quizResult.passPct}%, try again`}
                  </p>
                )}
                <p className="text-xs text-slate-400 mt-2">Grading runs when staff take this from an assigned training — correct answers are never shown here.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}


// Lesson images live in private storage — fetch with auth, render as blob.
function LessonImage({ path, alt }: { path?: string; alt: string }) {
  const { data: src } = useQuery({
    queryKey: ['lesson-image', path],
    enabled: !!path,
    staleTime: 3600_000,
    queryFn: async () => {
      const res = await api.get(path as string, { responseType: 'blob' });
      return URL.createObjectURL(res.data);
    },
  });
  if (!path || !src) return null;
  return <img src={src} alt={alt} className="rounded-lg mt-2 max-h-56 object-cover w-full" />;
}
